import {
  inject,
  Injectable,
  ViewContainerRef,
  type ComponentRef,
} from '@angular/core';
import { of, Subject } from 'rxjs';
import { delay, observeOn, tap } from 'rxjs/operators';

import { DomService } from './dom.service';
import { EnterZoneScheduler, LeaveZoneScheduler } from './helper';
import { NotificationContainerComponent } from './notification-container.component';
import { NotificationComponent } from './notification.component';

export enum NotificationStatus {
  Success = 'success',
  Error = 'error',
  Info = 'info',
}

export interface INotification {
  id?: number;
  message?: string;
  status?: NotificationStatus;
  fadein?: boolean;
  hover?: boolean;
}

export interface INotificationParams {
  message: string;
  status: NotificationStatus;
  number: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private _domService = inject(DomService);
  private _leaveZoneScheduler = inject(LeaveZoneScheduler);
  private _enterZoneScheduler = inject(EnterZoneScheduler);

  private _inElement: ViewContainerRef;

  public set inElement(element: ViewContainerRef) {
    this._inElement = element;
  }

  private _notification$: Subject<number> = new Subject<number>();
  private _destroy$: Subject<number> = new Subject<number>();
  private _notificationRefs: ComponentRef<NotificationComponent>[] = [];

  constructor() {
    this._notification$
      .pipe(
        delay(3500, this._leaveZoneScheduler),
        observeOn(this._enterZoneScheduler),
      )
      .subscribe(number => {
        this._destroy$.next(number);
      });

    this._destroy$
      .pipe(
        tap(number => {
          this._notificationRefs
            .filter(nRef => !nRef.instance.hover)
            .filter(nRef => nRef.instance.number === number)
            .map(nRef => {
              nRef.instance.destroying = true;
              nRef.instance.detectChanges();
            });
        }),
        delay(300, this._leaveZoneScheduler),
        observeOn(this._enterZoneScheduler),
      )
      .subscribe(number => {
        this._notificationRefs
          .filter(nRef => !nRef.instance.hover)
          .filter(nRef => nRef.instance.number === number)
          .map(nRef => nRef.destroy());
      });

    const compRef = this._domService.shiftInBody(
      NotificationContainerComponent,
    );
    this.inElement = compRef.instance.viewContainerRef;
  }

  public success({
    message,
    number,
  }: {
    message: string;
    number: number;
  }): ComponentRef<any> {
    return this.notify({
      message,
      number,
      status: NotificationStatus.Success,
    });
  }

  public error({
    message,
    number,
  }: {
    message: string;
    number: number;
  }): ComponentRef<any> {
    return this.notify({
      message,
      number,
      status: NotificationStatus.Error,
    });
  }

  public info({
    message,
    number,
  }: {
    message: string;
    number: number;
  }): ComponentRef<any> {
    return this.notify({
      message,
      number,
      status: NotificationStatus.Info,
    });
  }

  public notify({
    message,
    status,
    number,
  }: INotificationParams): ComponentRef<any> {
    const compRef = this._domService.shiftIn({
      component: NotificationComponent,
      inTarget: this._inElement,
    });
    this._notificationRefs.push(compRef);
    compRef.instance.message = message;
    compRef.instance.status = status;
    compRef.instance.number = number;

    this._notification$.next(number);

    return compRef;
  }

  public onClose(number: number): void {
    this._notificationRefs
      .filter(nRef => nRef.instance.number === number)
      .forEach(nRef => {
        nRef.instance.hover = false;
        nRef.instance.detectChanges();
      });

    this._destroy$.next(number);
  }

  public onMouseEnter(number: number): void {
    this._notificationRefs
      .filter(nRef => nRef.instance.number === number)
      .forEach(nRef => {
        nRef.instance.hover = true;
        nRef.instance.detectChanges();
      });
  }

  public onMouseLeave(number: number): void {
    of(null)
      .pipe(
        delay(1500, this._leaveZoneScheduler),
        observeOn(this._enterZoneScheduler),
      )
      .subscribe(() => {
        this._notificationRefs
          .filter(nRef => nRef.instance.number === number)
          .forEach(nRef => {
            nRef.instance.hover = false;
            nRef.instance.detectChanges();
          });
        this._destroy$.next(number);
      });
  }
}
